import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { IUnit } from "@app/interfaces/IPeripheral";
import { IPlot } from "@app/interfaces/IPlot";
import { IVehicle } from "@app/interfaces/IVehicle";
import { IVehicleUnitMapping } from "@app/interfaces/IVehiclePeripheralMapping";
import { combineLatest, forkJoin, Observable, of } from "rxjs";
import { map, mergeMap, switchMap } from "rxjs/operators";
import { ISerialSearchResponse } from "../_models/ISerialSearchResponse";
import { HardwareMappingService } from "../_routes/hardware/_services/hardware-mapping.service";
import { FoundationVehiclesService } from "./vehicle.service";

@Injectable({
    providedIn: 'root'
})
export class UnitsService {
        
    constructor(
        private readonly http: HttpClient,
        private readonly hardwareMappingService: HardwareMappingService,
        private readonly vehicleService: FoundationVehiclesService) { }

    getAllUnits(): Observable<IUnit[]> {
        return this.http.get<IUnit[]>(`/api/foundation/units`).pipe(mergeMap(units => {
            return this.hardwareMappingService.getProductsAndMap(units);
        }));
    }

    getAllUnitsWithMappings(): Observable<IUnit[]> {
        return combineLatest([
            this.getAllUnits(),
            this.getAllUnitMappings(),
            this.vehicleService.getVehicles()
        ]).pipe(
            mergeMap(([units, mappings, vehicles]) => {
                const vehiclesMap = new Map(vehicles.map(vehicle => [vehicle.Id, vehicle]));
                mappings.forEach(mapping => mapping.vehicle = vehiclesMap.get(mapping.VehicleId));
                const unitMappingsMap = new Map(mappings.map(mapping => [mapping.Serial, mapping]));
                const unitsWithUnitMappings = this.hardwareMappingService.applyVehicleMappingsToUnits(units, unitMappingsMap);
                return this.hardwareMappingService.getProductsAndMap(unitsWithUnitMappings);
            }));
    }

    getUnitBySerialWithMappings(serial: string): Observable<ISerialSearchResponse[]> {
        return combineLatest([
            this.getUnitBySerial(serial),
            this.getVehicleMappingsForUnit(serial),
            this.vehicleService.getVehicles()
        ]).pipe(
            mergeMap(([searchResponse, mappings, vehicles]) => {
                const vehiclesMap = new Map(vehicles.map(vehicle => [vehicle.Id, vehicle]));
                mappings.forEach(mapping => mapping.vehicle = vehiclesMap.get(mapping.VehicleId));
                const unitMappingsMap = new Map(mappings.map(mapping => [mapping.Serial, mapping]));
                const unitsWithUnitMappings = this.hardwareMappingService.applyVehicleMappingsToUnits([searchResponse.Unit], unitMappingsMap);
                return this.hardwareMappingService.getProductsAndMap(unitsWithUnitMappings);
            }));
    }

    getUnitBySerial(serial: string): Observable<ISerialSearchResponse> {
        return this.http.get<ISerialSearchResponse>(`/api/foundation/units/${serial}`);
    }

    getAllUnitMappings(): Observable<IVehicleUnitMapping[]> {
        return this.http.get<IVehicleUnitMapping[]>(`/api/foundation/vehicles/unitmappings`);
    }

    getUnitMappingsForVehicle(vehicleId: number): Observable<IVehicleUnitMapping[]> {
        return this.http.get<IVehicleUnitMapping[]>(`/api/foundation/vehicles/${vehicleId}/unitmappings`).pipe(map(mappings => {
            return mappings.filter(mapping => mapping.VehicleId === vehicleId);
        }));
    }

    getVehicleMappingsForUnit(serial: string): Observable<IVehicleUnitMapping[]> {
        return this.http.get<IVehicleUnitMapping[]>(`/api/foundation/vehicles/${serial}/allunitmappings`);
    }

    getUnit(vehicleId: number): Observable<IUnit> {
        return this.http.get<IUnit>(`/api/foundation/vehicles/${vehicleId}/unit`);
    }

    getUnitPlotsByImei(imei: string, startTime: Date, endTime: Date): Observable<IPlot[]> {
        return this.http.get<IPlot[]>(`/api/foundation/units/${imei}/plots`, {
            params: {
                start: startTime.toISOString(),
                end: endTime.toISOString()
            }
        })
    }

    getLatestUnitPlotsByImei(imei: string, amount: number = 10): Observable<IPlot[]> {
        return this.http.get<IPlot[]>(`/api/foundation/units/${imei}/latestplots`, {
            params: {
                amount: amount
            }
        });
          
    }

    getVehicleCurrentUnit(vehicle: IVehicle): Observable<any | IUnit> {
        return forkJoin({
            unitMappings: this.getUnitMappingsForVehicle(vehicle.Id),
            unit: this.getUnit(vehicle.Id)
        }).pipe(switchMap(data => {
            if (!data.unit) {
                return of(null);
            }

            return this.hardwareMappingService.getProductsAndMap([data.unit]).pipe(map(units => {
                data.unit.unitLocation = vehicle.UnitLocation ?? null;
                data.unit.StartUtc = data.unitMappings.find(x => x.Serial == data.unit.Serial)?.StartUtc;
                return data.unit;
            }))
        }));
    }

}
